import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';

import { ApiToken } from 'apis/auth';
import { ChatBot, chatbotsApi } from 'apis/chatbots';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCopy } from 'utils/useCopy';

type ThirdPartyTableActionsProps = {
  chatbot: ChatBot;
  tokens: readonly ApiToken[];
};

export const ThirdPartyTableActions = ({
  chatbot,
  tokens,
}: ThirdPartyTableActionsProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const [copyToClipboard] = useCopy();

  const [deleteChatbot] = chatbotsApi.useDeleteChatbotMutation();

  const handleViewChatbot = async () => {
    await dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:connectChatbotModal',
          arg: { chatbot },
        },
      }),
    );
  };

  const handleCopyToken = () => {
    if (chatbot._type !== 'ada') {
      return;
    }

    const apiToken = tokens.find((token) => token.id === chatbot.apiTokenId);
    if (!apiToken) {
      notify(
        {
          title: `Failed to find the "${chatbot.name ?? 'Chatbot'}" token`,
          text: 'The token was not found. If the error persist, try creating a new Chatbot.',
          time: 5000,
        },
        NotificationType.Error,
      );

      return;
    }

    copyToClipboard(apiToken.value);
    notify(
      {
        title: `"${chatbot.name ?? 'Chatbot'}" token copied`,
        text: 'The token is copied to your clipboard.',
        icon: 'i-done',
        time: 3000,
      },
      NotificationType.Success,
    );
  };

  const handleDeleteChatbot = async () => {
    ampli.chatbotDeleteModalOpened();
    const name = chatbot.name || 'Chatbot';

    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${name}"`,
            description: 'Are you sure you want to delete this chatbot?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      try {
        await deleteChatbot({ id: chatbot.id });
        ampli.chatbotDeleted({ chatbotType: chatbot._type });
        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          { title: `Error while deleting "${name}".` },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <>
      {chatbot._type === 'ada' ? (
        <>
          <Tooltip content="View chatbot">
            <IconButton
              variant="tertiary"
              icon="eye"
              onClick={handleViewChatbot}
            />
          </Tooltip>
          <Tooltip content="Copy token">
            <IconButton
              variant="tertiary"
              icon="copy"
              onClick={handleCopyToken}
            />
          </Tooltip>
        </>
      ) : null}
      <Tooltip content="Delete">
        <IconButton
          variant="tertiary"
          icon="trash"
          onClick={handleDeleteChatbot}
        />
      </Tooltip>
    </>
  );
};
